import React, { useState, useEffect } from "react";
import { Layout, Menu, Card, Typography } from "antd";
import { motion, AnimatePresence } from "framer-motion";
import pose1 from "../../Assets/meditation/pose1.jpg";
import pose2 from "../../Assets/meditation/pose2.jpg";
import pose3 from "../../Assets/meditation/pose3.jpg";
import pose4 from "../../Assets/meditation/pose4.jpg";
import pose5 from "../../Assets/meditation/pose5.jpg";
import pose6 from "../../Assets/meditation/pose6.jpg";
import pose7 from "../../Assets/meditation/pose7.jpg";

const { Sider, Content } = Layout;
const { Title, Paragraph } = Typography;

const DummyMeditation = () => {
  const [selectedKey, setSelectedKey] = useState("home");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const handleLeftMenu = (e) => setSelectedKey(e.key);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fadeSlide = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
    exit: { opacity: 0, y: -20, transition: { duration: 0.4 } },
  };
  const pages = {
    home: (
      <>
        <div style={{ lineHeight: "1.8", color: "#222", fontSize: "16px" }}>
          <Title level={2} style={{ textAlign: "center" }}>
            What is Meditation?
          </Title>
          <Paragraph
            style={{
              textAlign: "center",
              maxWidth: 700,
              margin: "10px auto",
              lineHeight: 1.7,
            }}
          >
            Amidst the chaotic demands of modern life, finding peace of mind is
            a challenge. Meditation is a mental practice that focuses the mind
            on a specific state and calms the mind. It is also an achievement
            that can be used to develop our inner power. It is not related to
            any religion, but a life skill that everyone can learn. It is an art
            that has been practiced in Eastern countries, including India, for
            thousands of years.
          </Paragraph>
          <ul>
            <li>To achieve peace and tranquility.</li>
            <li>
              To become aware of the constant flow of thoughts in the mind and
              to get rid of them.
            </li>
            <li>To develop self-awareness.</li>
            <li>To improve focus.</li>
            <li>To reduce stress.</li>
          </ul>
          <h3 style={{ color: "#0c5c78", marginTop: "20px" }}>
            Types of meditation:
          </h3>
          <p>
            <b>Focus Meditation</b>
          </p>
          <ul style={{ paddingLeft: "20px" }}>
            <li>Focusing on the breath</li>
            <li>Chanting a mantra/word</li>
            <li>Looking at a candle flame</li>
          </ul>
          <p>
            <b>Mindfulness</b>
          </p>
          <ul style={{ paddingLeft: "20px" }}>
            <li>Living in the present moment</li>
            <li>Noticing thoughts (but not getting caught up in them)</li>
          </ul>
          <p>
            <b>Love-Kindness Meditation</b>
          </p>
          <ul style={{ paddingLeft: "20px" }}>
            <li>Developing love for yourself and others</li>
            <li>Thinking "May all beings live happily"</li>
          </ul>
          <h3 style={{ color: "#0c5c78", marginTop: "20px" }}>
            Benefits of meditation:
          </h3>
          <p>
            <b>For the mind:</b>
          </p>
          <ul style={{ paddingLeft: "20px" }}>
            <li>Reduced stress</li>
            <li>Increased focus and memory</li>
            <li>Control of thoughts</li>
            <li>Peace and contentment</li>
          </ul>
          <p>
            <b>For the body:</b>
          </p>
          <ul style={{ paddingLeft: "20px" }}>
            <li>Reduced blood pressure</li>
            <li>Increased immunity</li>
            <li>Relief from insomnia</li>
            <li>Delayed aging</li>
          </ul>
          <h3 style={{ color: "#0c5c78", marginTop: "20px" }}>
            Simple meditation method (beginner level):
          </h3>
          <ul style={{ paddingLeft: "20px" }}>
            <li>
              <b> Place:</b> Choose a quiet place
            </li>
            <li>
              <b>Position:</b> Sit in Sukasana/Padmasana
            </li>
            <li>
              <b>Eyes:</b> Closed or half-closed Position
            </li>
            <li>
              <b>Back:</b> Keep your back straight
            </li>
          </ul>
          <p>
            <b>Steps:</b>
          </p>
          <ul style={{ paddingLeft: "20px" }}>
            <li>Take 5 deep breaths and exhale</li>
            <li>Notice the natural inflow and outflow of your breath</li>
            <li>Focus on your nostrils/chest/lungs</li>
            <li>If thoughts come, bring them back to your breath</li>
            <li>
              <b>Start:</b> 5-10 minutes a day
            </li>
            <li>
              <b>Then:</b> Gradually increase the time
            </li>
          </ul>
          <h3 style={{ color: "#0c5c78", marginTop: "20px" }}>
            Key principles:
          </h3>
          <ul style={{ paddingLeft: "20px" }}>
            <li>Don't control your thoughts - just observe</li>
            <li>There is no such thing as "failure" in meditation</li>
            <li>Fatigue/fatigue is natural</li>
            <li>Meditation is for the experience, not for the goal</li>
          </ul>
          <h3 style={{ color: "#0c5c78", marginTop: "20px" }}>
            Common mistakes:
          </h3>
          <ul style={{ paddingLeft: "20px" }}>
            <li>Expecting "the mind to be quiet"</li>
            <li>Expecting results too quickly</li>
            <li>Irregular practice</li>
            <li>Forcing a meditative state</li>
            <li>
              Meditation is a <b>"journey of experience"</b> - not a way to
              reach a goal. With daily practice, you can experience many
              wonderful changes!
            </li>
            <li>Good! I will give you more details about meditation.</li>
          </ul>
          <h3 style={{ color: "#0c5c78", marginTop: "20px" }}>
            Scientific aspects of meditation:
          </h3>
          <p>
            <b>Changes in the brain:</b>
          </p>
          <ul style={{ paddingLeft: "20px" }}>
            <li>Amygdala (fear/stress center) - shrinks</li>
            <li>Prefrontal cortex (decision-making center) - thickens</li>
            <li>Serotonin (happiness hormone) - increases</li>
            <li>Cortisol (stress hormone) - decreases</li>
          </ul>
          <h3 style={{ color: "#0c5c78", marginTop: "20px" }}>
            Benefits of meditation in daily life:
          </h3>
          <p>
            <b>In work/study:</b>
          </p>
          <ul style={{ paddingLeft: "20px" }}>
            <li>Increase in attention span</li>
            <li>Improvement in problem-solving skills</li>
            <li>Creative thinking</li>
            <li>Decision-making skills</li>
          </ul>
          <p>
            <b>In relationships:</b>
          </p>
          <ul style={{ paddingLeft: "20px" }}>
            <li>Increase in patience</li>
            <li>Anger control</li>
            <li>Ability to understand other people's feelings</li>
            <li>Improvement in communication skills</li>
          </ul>
          <h3 style={{ color: "#0c5c78", marginTop: "20px" }}>
            Advanced meditation methods:
          </h3>
          <ul style={{ paddingLeft: "20px" }}>
            <li>
              <b>Vipassana meditation</b> - observing body sensations
            </li>
            <li>
              <b>Sotan meditation</b> - seeing the natural light of the mind
            </li>
            <li>
              <b>Drasakam</b> - focusing on external objects without closing the
              eyes
            </li>
            <li>
              <b>Natham meditation</b> - focusing on internal sounds
            </li>
          </ul>
          <h3 style={{ color: "#0c5c78", marginTop: "20px" }}>
            Tools to help with meditation:
          </h3>
          <ul style={{ paddingLeft: "20px" }}>
            <li>Malais/Jabamala - to hold attention</li>
            <li>Mantras - "Om", "Sam", "Amma"</li>
            <li>Nature sounds - rain, waterfall, birdsong</li>
            <li>Brush/Sandal - Mind-blowing fragrance</li>
          </ul>
          <h3 style={{ color: "#0c5c78", marginTop: "20px" }}>
            7 steps of meditation (bottle analogy):
          </h3>
          <ul style={{ paddingLeft: "20px" }}>
            <li>
              <b>Asana</b> - steady, comfortable posture
            </li>
            <li>
              <b>Pranayama</b> - regulating breathing
            </li>
            <li>
              <b>Pratyakara</b> - focusing the senses
            </li>
            <li>
              <b>Dharana</b> - focusing attention
            </li>
            <li>
              <b>Dhyana</b> - continuous flow of attention
            </li>
            <li>
              <b>Samadhi</b> -Union status
            </li>
            <li>
              <b>Kevalam</b> - natural state
            </li>
          </ul>
          <h3 style={{ color: "#0c5c78", marginTop: "20px" }}>Daily tips:</h3>
          <ul style={{ paddingLeft: "20px" }}>
            <li>
              <b>Morning:</b> 10-15 minutes (to start the day)
            </li>
            <li>
              <b>Noon:</b> 5 minutes (to relieve fatigue)
            </li>
            <li>
              <b>Evening:</b> 10 minutes (to end the day)
            </li>
          </ul>
          <h3 style={{ color: "#0c5c78", marginTop: "20px" }}>
            Solutions to the challenges you face:
          </h3>
          <ul style={{ paddingLeft: "20px" }}>
            <li>
              <b>"No time"</b> - Start with just 5 minutes
            </li>
            <li>
              <b>"No mind"</b> - That's the purpose of meditation!
            </li>
            <li>
              <b>"Rapid thoughts"</b> - Let them come and go like clouds
            </li>
            <li>
              <b>"Physical discomfort"</b> - You can do it sitting in a chair
            </li>
          </ul>
          <h3 style={{ color: "#0c5c78", marginTop: "20px" }}>
            Meditation for children:
          </h3>
          <ul style={{ paddingLeft: "20px" }}>
            <li>
              <b> "Balloon Breath"</b> - Like blowing up a balloon with your
              breath
            </li>
            <li>
              <b>"My heartbeat"</b> - Notice physical sensations
            </li>
            <li>
              <b>"Sending love"</b> - Imagine love for living beings
            </li>
          </ul>
          <h3 style={{ color: "#0c5c78", marginTop: "20px" }}>
            Modern meditation apps:
          </h3>
          <ul style={{ paddingLeft: "20px" }}>
            <li>
              <b>Meditation apps:</b> Headspace, Calm, Insight Timer
            </li>
            <li>Guided meditation (YouTube/Spotify)</li>
            <li>Phone reminder notifications - Help you meditate</li>
            <li>
              <b>Remember:</b> Meditation is a journey - not a destination.
              Small steps every day can make a big difference!
            </li>
          </ul>
          <h3 style={{ color: "#0c5c78", marginTop: "20px" }}>
            Basic steps of meditation (simple method):
          </h3>
          <ol style={{ paddingLeft: "20px" }}>
            <li>
              <b>Preparation:</b>
            </li>
            <ul>
              <li>
                <b>Place:</b> Quiet, cool, clean place
              </li>
              <li>
                <b>Time:</b> 4-6 am (Brahma Mukhurtham) or evening
              </li>
              <li>
                <b>Asana:</b> Padmasana, Sukhasana, can be done sitting on a
                chair
              </li>
            </ul>
            <li>
              <b>Body position:</b>
            </li>
            <ul>
              <li>Back should be straight</li>
              <li>Hands should be in Jnana Mudra or on knees</li>
              <li>Should be relaxed</li>
              <li>Slight smile on face</li>
            </ul>
            <li>
              <b>Breathing exercise:</b>
            </li>
            <ul>
              <li>Take 5 deep breaths-release</li>
              <li>Then do natural breathing Observing</li>
            </ul>
          </ol>
          <h3 style={{ color: "#0c5c78", marginTop: "20px" }}>
            5 golden rules of meditation:
          </h3>
          <ul style={{ paddingLeft: "20px" }}>
            <li>
              <b>Releasing</b> - Don't hold on to thoughts
            </li>
            <li>
              <b>Attention</b> - You are not the thoughts, you are the observer
            </li>
            \
            <li>
              <b>Reflection</b> - Focus on the breath repeatedly
            </li>
            <li>
              <b>Don't force it</b> - Let it come naturally
            </li>
            <li>
              <b>Thankfulness</b> - Give thanks after each meditation
            </li>
          </ul>
          <h3 style={{ color: "#0c5c78", marginTop: "20px" }}>
            Points of focus in meditation (you can try each):
          </h3>
          <ul style={{ paddingLeft: "20px" }}>
            <li>Tip of the nose - Sensation of the breath coming and going</li>
            <li>Between the eyebrows - Anjali Chakra</li>
            <li>Thrust - Manipura Chakra</li>
            <li>Heart - Anahata Chakra</li>
            <li>Mantra - "Om", "So Hum", "Amma"</li>
          </ul>
          <h3 style={{ color: "#0c5c78", marginTop: "20px" }}>
            Experiences during meditation:
          </h3>
          <p>
            <b>Normal experiences:</b>
          </p>
          <ul style={{ paddingLeft: "20px" }}>
            <li>Feeling of lightness in the body</li>
            <li>Warmth/coldness in the hands and feet</li>
            <li>Slight dizziness</li>
            <li>Feeling of the body swinging</li>
          </ul>
          <p>
            <b>Advanced experiences:</b>
          </p>
          <ul style={{ paddingLeft: "20px" }}>
            <li>Lost sense of time and space</li>
            <li>A sense of peaceful bliss</li>
            <li>Seeing light/colors</li>
            <li>A sense of unity</li>
          </ul>
          <h3 style={{ textAlign: "center" }}>Problems faced & solutions:</h3>
          <div
            style={{
              overflowX: "auto",
              border: "1px solid #ccc",
              borderRadius: "8px",
              marginTop: "10px",
              marginBottom: "20px",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                textAlign: "left",
                fontSize: "15px",
              }}
            >
              <thead>
                <tr style={{ background: "#f4f9f9" }}>
                  <th style={{ border: "1px solid #ddd", padding: "10px" }}>
                    Problem
                  </th>
                  <th style={{ border: "1px solid #ddd", padding: "10px" }}>
                    Solution
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ border: "1px solid #ddd", padding: "10px" }}>
                    Mind wandering
                  </td>
                  <td style={{ border: "1px solid #ddd", padding: "10px" }}>
                    Refocusing on breath
                  </td>
                </tr>
                <tr>
                  <td style={{ border: "1px solid #ddd", padding: "10px" }}>
                    Physical discomfort
                  </td>
                  <td style={{ border: "1px solid #ddd", padding: "10px" }}>
                    Change in posture
                  </td>
                </tr>
                <tr>
                  <td style={{ border: "1px solid #ddd", padding: "10px" }}>
                    Drowsiness
                  </td>
                  <td style={{ border: "1px solid #ddd", padding: "10px" }}>
                    Meditation with eyes open
                  </td>
                </tr>
                <tr>
                  <td style={{ border: "1px solid #ddd", padding: "10px" }}>
                    Lack of patience
                  </td>
                  <td style={{ border: "1px solid #ddd", padding: "10px" }}>
                    Start with just 5 minutes
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            Can't do it right" There is no such thing as "proper meditation"
          </p>
          <h3 style={{ color: "#0c5c78", marginTop: "20px" }}>
            Daily Meditation Routine (Practice):
          </h3>
          <p>
            <b>Morning:</b>
          </p>
          <p>
            4:30-5:00 - Wake up and take a bath
            <br />
            5:00-5:30 - Pranayama
            <br />
            5:30-6:00 - Meditation
          </p>
          <p>
            <b>Evening:</b>
          </p>
          <p>
            6:00-6:30 - Savasana/Rest
            <br />
            6:30-7:00 - Meditation
          </p>
          <h3 style={{ color: "#0c5c78", marginTop: "20px" }}>Weekly Plan:</h3>
          <p>
            Monday: Breathing Meditation
            <br />
            Tuesday: Mantra Chanting
            <br />
            Wednesday: Loving-Kindness Meditation
            <br />
            Thrusday: Vipassana Meditation
            <br />
            Friday: Light Meditation
            <br />
            Saturday: Ascetic Meditation
            <br />
            Sunday: Self-Experiential Meditation
          </p>
          <h3 style={{ color: "#0c5c78", marginTop: "20px" }}>
            Special Meditation Techniques:
          </h3>
          <ul style={{ paddingLeft: "20px" }}>
            <li>"I am not" Meditation - "Who am I?" Asking</li>
            <li>Laughter Meditation - Laugh for 5 minutes</li>
            <li>Walking Meditation - Paying attention while walking</li>
            <li>Meal Meditation - Paying full attention while eating</li>
          </ul>
          <h3 style={{ color: "#0c5c78", marginTop: "20px" }}>
            Important Facts:
          </h3>
          <ul style={{ paddingLeft: "20px" }}>
            <li>There is no "win-lose" in meditation</li>
            <li>Every meditation is a different experience</li>
            <li>Expecting results will not bring results</li>
            <li>Continued results will come</li>
            <li>Meditation is a skill that can grow throughout life</li>
            <li>Start at your own pace!</li>
            <li>"You don't see how deep you are - success is in trying!"</li>
          </ul>
        </div>
      </>
    ),
    left1: (
      <>
        <div style={{ lineHeight: "1.8", color: "#222", fontSize: "16px" }}>
          <Title level={2} style={{ textAlign: "center" }}>
            1. Padmasana (Lotus Pose)
          </Title>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card
              style={{
                borderRadius: "14px",
                padding: "20px",
                boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: window.innerWidth < 768 ? "column" : "row",
                  gap: "20px",
                }}
              >
                {/* LEFT: IMAGE */}
                <img
                  src={pose1}
                  alt="Padmasana"
                  style={{
                    width: window.innerWidth < 768 ? "100%" : "45%",
                    height: "auto",
                    borderRadius: "10px",
                    objectFit: "cover",
                  }}
                />

                {/* RIGHT: TEXT */}
                <div style={{ flex: 1 }}>
                  <Paragraph>
                    <strong>Explanation:</strong> This is a traditional
                    meditation asana.
                  </Paragraph>

                  <Paragraph>
                    <strong>Benefits:</strong> Stabilizes the mind, improves
                    focus, and relieves back and neck pain.
                  </Paragraph>

                  <Paragraph>
                    <strong>Procedure:</strong>
                  </Paragraph>

                  <ul style={{ lineHeight: 1.8 }}>
                    <li>Sit straight on the floor and stretch your legs.</li>
                    <li>
                      Place the right foot on the left thigh and the left foot
                      on the right thigh.
                    </li>
                    <li>
                      Keep your back straight and place your hands on your knees
                      in Gnana Mudra or Chin Mudra.
                    </li>
                  </ul>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </>
    ),
    left2: (
      <>
        <div style={{ lineHeight: "1.8", color: "#222", fontSize: "16px" }}>
          <Title level={2} style={{ textAlign: "center" }}>
            2. Sukasana (Easy Pose)
          </Title>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card
              style={{
                borderRadius: "14px",
                padding: "20px",
                boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: window.innerWidth < 768 ? "column" : "row",
                  gap: "20px",
                }}
              >
                {/* LEFT: IMAGE */}
                <img
                  src={pose2}
                  alt="Sukasana"
                  style={{
                    width: window.innerWidth < 768 ? "100%" : "45%",
                    height: "auto",
                    borderRadius: "10px",
                    objectFit: "cover",
                  }}
                />

                {/* RIGHT: TEXT */}
                <div style={{ flex: 1 }}>
                  <Paragraph>
                    <strong>Explanation:</strong> Suitable for those who have
                    difficulty with Padmasana.
                  </Paragraph>

                  <Paragraph>
                    <strong>Benefits:</strong> Provides peace of mind and keeps
                    the body in balance.
                  </Paragraph>

                  <Paragraph>
                    <strong>Procedure:</strong>
                  </Paragraph>

                  <ul style={{ lineHeight: 1.8 }}>
                    <li>Sit on the floor and cross your legs.</li>
                    <li>Each leg should be under the opposite thigh.</li>
                    <li>
                      Keep your back straight and place your hands on your
                      knees.
                    </li>
                  </ul>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </>
    ),
    left3: (
      <>
        <div style={{ lineHeight: "1.8", color: "#222", fontSize: "16px" }}>
          <Title level={2} style={{ textAlign: "center" }}>
            3. Vajrasana (Diamond Pose)
          </Title>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card
              style={{
                borderRadius: "14px",
                padding: "20px",
                boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: window.innerWidth < 768 ? "column" : "row",
                  gap: "20px",
                }}
              >
                {/* LEFT: IMAGE */}
                <img
                  src={pose3}
                  alt="Vajrasana"
                  style={{
                    width: window.innerWidth < 768 ? "100%" : "45%",
                    height: "auto",
                    borderRadius: "10px",
                    objectFit: "cover",
                  }}
                />

                {/* RIGHT: TEXT */}
                <div style={{ flex: 1 }}>
                  <Paragraph>
                    <strong>Explanation:</strong> Suitable to be done after
                    cooking or eating.
                  </Paragraph>

                  <Paragraph>
                    <strong>Benefits:</strong> Promotes digestion and keeps the
                    mind in balance.
                  </Paragraph>

                  <Paragraph>
                    <strong>Procedure:</strong>
                  </Paragraph>

                  <ul style={{ lineHeight: 1.8 }}>
                    <li>First sit on your knees.</li>
                    <li>Sit on your heels.</li>
                    <li>
                      Keep your back straight and place your hands on your
                      thighs.
                    </li>
                  </ul>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </>
    ),
    left4: (
      <>
        <div style={{ lineHeight: "1.8", color: "#222", fontSize: "16px" }}>
          <Title level={2} style={{ textAlign: "center" }}>
            4. Siddhasana (Complete Pose)
          </Title>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card
              style={{
                borderRadius: "14px",
                padding: "20px",
                boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: window.innerWidth < 768 ? "column" : "row",
                  gap: "20px",
                }}
              >
                {/* LEFT: IMAGE */}
                <img
                  src={pose4}
                  alt="Siddhasana"
                  style={{
                    width: window.innerWidth < 768 ? "100%" : "45%",
                    height: "auto",
                    borderRadius: "10px",
                    objectFit: "cover",
                  }}
                />

                {/* RIGHT: TEXT */}
                <div style={{ flex: 1 }}>
                  <Paragraph>
                    <strong>Explanation:</strong> Considered to be very suitable
                    for meditation.
                  </Paragraph>

                  <Paragraph>
                    <strong>Benefits:</strong> Calms the nervous system and
                    controls the mind.
                  </Paragraph>

                  <Paragraph>
                    <strong>Procedure:</strong>
                  </Paragraph>

                  <ul style={{ lineHeight: 1.8 }}>
                    <li>
                      Place the heel of one leg between the thigh and the space
                      under the other leg.
                    </li>
                    <li>
                      Keep your back straight and place your hands on your
                      knees.
                    </li>
                  </ul>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </>
    ),
    left5: (
      <>
        <div style={{ lineHeight: "1.8", color: "#222", fontSize: "16px" }}>
          <Title level={2} style={{ textAlign: "center" }}>
            5. Savasana (Corpse Pose)
          </Title>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card
              style={{
                borderRadius: "14px",
                padding: "20px",
                boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
              }}
            >
              <div
                style={{
                  display: "grid",
                  flexDirection: window.innerWidth < 768 ? "column" : "row",
                  gap: "20px",
                }}
              >
                {/* LEFT: IMAGE */}
                <img
                  src={pose5}
                  alt="Savasana"
                  style={{
                    width: window.innerWidth < 768 ? "100%" : "45%",
                    height: "auto",
                    borderRadius: "10px",
                    objectFit: "cover",
                    margin: "0 auto",
                  }}
                />

                {/* RIGHT: TEXT */}
                <div style={{ flex: 1 }}>
                  <Paragraph>
                    <strong>Explanation:</strong> A meditation done while
                    leaning back on the bed.
                  </Paragraph>

                  <Paragraph>
                    <strong>Benefits:</strong> Reduces stress and calms the
                    mind.
                  </Paragraph>

                  <Paragraph>
                    <strong>Procedure:</strong>
                  </Paragraph>

                  <ul style={{ lineHeight: 1.8 }}>
                    <li>Lie back and place your feet slightly apart.</li>
                    <li>Close your eyes and breathe deeply.</li>
                  </ul>
                  <Paragraph>
                    <strong>General notes:</strong> <br />
                    <b>Back straight:</b> Your back should be straight in any
                    asana.
                    <br />
                    <b>Relaxation:</b> Your body parts should be relaxed.
                    <br />
                    <b>Breathing:</b>Your breathing should be natural.
                    <br />
                    <b>Choice:</b>Choose an asana that is comfortable for you.
                    <br />
                    Initially, you should meditate for only a short time.
                    Gradually increase the time.
                  </Paragraph>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </>
    ),
    left6: (
      <>
        <div style={{ lineHeight: "1.8", color: "#222", fontSize: "16px" }}>
          <Title level={2} style={{ textAlign: "center" }}>
            6. Vrikshasana or Tree Pose
          </Title>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card
              style={{
                borderRadius: "14px",
                padding: "20px",
                boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: window.innerWidth < 768 ? "column" : "row",
                  gap: "20px",
                }}
              >
                {/* LEFT: IMAGE */}
                <img
                  src={pose6}
                  alt="Vrikshasana"
                  style={{
                    width: window.innerWidth < 768 ? "100%" : "45%",
                    height: "auto",
                    borderRadius: "10px",
                    objectFit: "cover",
                  }}
                />

                {/* RIGHT: TEXT */}
                <div style={{ flex: 1 }}>
                  <Paragraph>
                    <strong> Vrikshasana (Tree Pose)</strong> is a very helpful
                    asana for meditation, especially the breathing exercises
                    done in it can significantly improve the quality of
                    meditation.
                    <br />
                    Because Vrikshasana and its breathing exercises fulfill the
                    basic needs of our mind:
                    <br />
                    People with leg pain and joint pain should not do this
                    asana. This asana helps to stabilize the mind. Since this
                    asana is done standing on one leg, it is important to be
                    balanced.
                  </Paragraph>

                  <Paragraph>
                    <b>1. Focus</b>
                  </Paragraph>
                  <p>
                    {" "}
                    When standing in Tree Pose, we feel a pull in the ankles,
                    feet, thighs, back - everywhere.
                  </p>
                  <p>
                    Your attention is completely drawn to the present moment to
                    maintain balance.
                  </p>
                  <p>
                    This is the first stage of meditation, 'Dharana'
                    (concentration of attention). Maintaining balance is a kind
                    of "mindfulness meditation."
                  </p>

                  <Paragraph>
                    <b>2. Controlling the flow of the mind</b>
                  </Paragraph>
                  <p>
                    Whenever we lose our balance, thoughts ("Oh, I'm going to
                    fall!", "My leg hurts") arise in the mind.
                  </p>
                  <p>
                    Through breathing exercises, we disengage from these
                    thoughts and return to the flow of the breath.
                  </p>
                  <p>
                    This trains us for the second stage of meditation, 'Dhyanam'
                    (Ottathiruttal). We observe our thoughts and remain focused
                    on our breath without getting caught up in them.
                  </p>

                  <Paragraph>
                    <b>3. Calming the nervous system</b>
                  </Paragraph>
                  <p>
                    {" "}
                    Deep and steady breathing helps us deal with the tremors and
                    tension that occur in the body when standing in Vrikshasana.
                  </p>
                  <p>
                    Long inhalations and exhalations activate our
                    Parasympathetic Nervous System, calming the mind and body.
                  </p>
                  <p>
                    Breathing Exercise in Vrikshasana - As a Meditation Exercise
                  </p>
                  <p>Here's how you can practice this asana as a meditation:</p>
                  <p>
                    <b>Posture:</b> Stand in Vrikshasana. Close your eyes (if
                    you find it difficult to maintain balance at first, you can
                    fix your eyes on one point).
                  </p>
                  <p>
                    <b>Attention to Body Sensations:</b> Feel the strength of
                    your body, the firmness of your feet on the ground, the
                    length of your back, and the stretch in your thighs.
                  </p>

                  <h3>Connecting the Breath:</h3>
                  <p>
                    <b>Inhale: </b>As you inhale deeply, you should feel a sense
                    of length and expansion in your body. You can mentally say
                    "length".
                  </p>
                  <p>
                    <b>Exhale:</b> As you exhale, you should feel your body
                    becoming more stable and rooted to the ground. You can
                    mentally say "steadiness".
                  </p>
                  <p>
                    <b>Meditate on loss of balance:</b> If you lose your
                    balance, welcome it without any hesitation or anger, and
                    return to the asana. This process is what meditation teaches
                    you about the life lesson of "falling back to normal".
                  </p>
                  <h3>Conclusion:</h3>
                  <p>
                    Vrikshasana, along with breathing exercises, is a "moving
                    meditation" or "standing meditation".
                    <b /> It is a great pre-practice for sitting still and
                    meditating. Vrikshasana trains the mental stability,
                    concentration, and control of breathing required for
                    meditation while sitting in fixed postures (Padmasana,
                    Sukhasana).
                    <b />
                    Therefore, breathing practice in Vrikshasana is definitely a
                    great help for meditation.
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </>
    ),
  };

  const selectedContent = pages[selectedKey];
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "stretch",
        minHeight: "100vh",
        height: "100%",
        padding: "30px 10px",
        background: "#f0f2f5",
      }}
    >
      <Layout
        style={{
          width: isMobile ? "100%" : "90%",
          maxWidth: "1340px",
          background: "#fff",
          borderRadius: "16px",
          boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          overflow: "hidden",
          height: "auto",
        }}
      >
        {/* 🌿 LEFT SIDEBAR */}
        <motion.div
          initial={{ x: -80, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          style={{
            width: isMobile ? "100%" : "200px",
            height: isMobile ? "auto" : "100%",
          }}
        >
          <Sider
            width={isMobile ? "100%" : 230}
            style={{
              position: isMobile ? "relative" : "sticky",
              top: isMobile ? "0px" : "64px", // FIXED
              marginTop: isMobile ? "60px" : "0px",
              height: isMobile ? "auto" : "calc(100vh - 64px)",
              minHeight: isMobile ? "250px" : "unset",

              backgroundImage: `url(${pose7})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              overflow: "hidden",
              borderRight: isMobile
                ? "none"
                : "1px solid rgba(255,255,255,0.2)",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0, 0, 0, 0.6)",
                zIndex: 1,
              }}
            />

            {/* 🌿 Sidebar Content */}
            <div
              style={{
                position: "relative",
                zIndex: 2,
                padding: "16px",
                fontWeight: "bold",
                textAlign: "center",
                color: "#fff",
                fontSize: "1rem",
                letterSpacing: "0.5px",
                textShadow: "0 1px 3px rgba(0,0,0,0.8)",
              }}
            >
              {" "}
              Suitable postures for meditation:
            </div>

            <Menu
              theme="dark"
              mode="inline"
              style={{
                background: "transparent",
                color: "#fff",
                fontSize: "1.1rem",
                position: "relative",
                zIndex: 2,
                fontWeight: 500,
                textAlign: "center",
              }}
              onClick={handleLeftMenu}
              selectedKeys={[selectedKey]}
              items={[
                { key: "left1", label: "Padmasana" },
                { key: "left2", label: "Sukasana" },
                { key: "left3", label: "Vajrasana" },
                { key: "left4", label: "Siddhasana" },
                { key: "left5", label: "Savasana" },
                { key: "left6", label: "Vrikshasana" },
                { key: "home", label: "Back to Mainpage" },
              ]}
            />
          </Sider>
        </motion.div>

        {/*  MAIN CONTENT */}
        <Content
          style={{
            flex: 1,
            padding: isMobile ? "10px" : "30px 40px",
            width: "100%",

            background: "#fff",
            textAlign: "left",
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedKey}
              variants={fadeSlide}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <Card
                style={{
                  borderRadius: "12px",
                  background: "#ffffff",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                  padding: "24px",
                }}
              >
                {selectedContent}
              </Card>
            </motion.div>
          </AnimatePresence>
        </Content>
      </Layout>
    </motion.div>
  );
};

export default DummyMeditation;
